import { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { callMethod } from '../../../common/utilsClient/ddp/actions';
import { apiZedocAllProcedures } from '../../../common/api/zedoc';
import { useRum } from '../../../containers/RumContext';
import { CALL_METHOD_TYPE } from '../../../store/realUserMonitoring';

/**
 * Custom hook to fetch procedures from OMOP database
 *
 * @param {Object} params - { page: number, pageSize: number, status: string, patientName: string, clinicianId: number, startDate: string, endDate: string }
 * @returns {Object} { procedures: Array, total: number, loading: boolean, error: Error | null, refetch: Function }
 */
export const useProcedures = ({
  page = 1,
  pageSize = 10,
  status,
  patientName,
  clinicianId,
  startDate,
  endDate,
  skip = false,
  view = 'table',
} = {}) => {
  const dispatch = useDispatch();
  const awsRum = useRum();
  const [procedures, setProcedures] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch procedures whenever page, pageSize, status, patientName, clinicianId, or date range changes
  useEffect(() => {
    if (skip) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);

    const apiParams = { page, pageSize };
    if (status) {
      apiParams.status = status;
    }
    if (patientName) {
      apiParams.patientName = patientName;
    }
    if (clinicianId) {
      apiParams.clinicianId = clinicianId;
    }
    if (startDate) {
      apiParams.startDate = startDate;
    }
    if (endDate) {
      apiParams.endDate = endDate;
    }

    const rumStart = Date.now();
    dispatch(callMethod(apiZedocAllProcedures, apiParams))
      .then((result) => {
        awsRum?.recordEvent(CALL_METHOD_TYPE, {
          duration: Date.now() - rumStart,
          name: 'api.zedoc.allProcedures',
          view,
          filtered: !!(
            status ||
            patientName ||
            clinicianId ||
            startDate ||
            endDate
          ),
          pageIndex: page - 1,
        });
        setLoading(false);
        if (result && typeof result === 'object' && 'data' in result) {
          setProcedures(result.data || []);
          setTotal(result.total || 0);
        } else {
          setProcedures(result || []);
          setTotal((result || []).length);
        }
      })
      .catch((err) => {
        setLoading(false);
        console.error('Error fetching procedures:', err);
        setError(err);
      });
  }, [
    awsRum,
    dispatch,
    page,
    pageSize,
    status,
    patientName,
    clinicianId,
    startDate,
    endDate,
    skip,
    view,
  ]);

  const refetch = useCallback(() => {
    setLoading(true);
    setError(null);

    const apiParams = { page, pageSize };
    if (status) {
      apiParams.status = status;
    }
    if (patientName) {
      apiParams.patientName = patientName;
    }
    if (clinicianId) {
      apiParams.clinicianId = clinicianId;
    }
    if (startDate) {
      apiParams.startDate = startDate;
    }
    if (endDate) {
      apiParams.endDate = endDate;
    }

    const rumStart = Date.now();
    dispatch(callMethod(apiZedocAllProcedures, apiParams))
      .then((result) => {
        awsRum?.recordEvent(CALL_METHOD_TYPE, {
          duration: Date.now() - rumStart,
          name: 'api.zedoc.allProcedures',
          view,
          filtered: !!(
            status ||
            patientName ||
            clinicianId ||
            startDate ||
            endDate
          ),
          pageIndex: page - 1,
        });
        setLoading(false);
        if (result && typeof result === 'object' && 'data' in result) {
          setProcedures(result.data || []);
          setTotal(result.total || 0);
        } else {
          setProcedures(result || []);
          setTotal((result || []).length);
        }
      })
      .catch((err) => {
        setLoading(false);
        console.error('Error fetching procedures:', err);
        setError(err);
      });
  }, [
    awsRum,
    dispatch,
    page,
    pageSize,
    status,
    patientName,
    clinicianId,
    startDate,
    endDate,
    view,
  ]);

  return {
    procedures,
    total,
    loading,
    error,
    refetch,
  };
};

export default useProcedures;
